import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { Avatar, IconButton, ListItemText, Menu, MenuItem, Stack, Tooltip, styled } from '@mui/material';
import { ReactSVG } from 'react-svg';
import { EditActiveOption } from '../../UIData/_actions/DesignLabStoreActions';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch';
import Immutable from 'immutable';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import type { LabOptionColor } from '../../AppData/LabOptionColor';
import { getLabOptionColorBackground, getLabOptionColors } from '../../AppData/LabOptionColor';

const ColorCircle = styled('div')({
  width: '40px',
  height: '40px',
  borderRadius: '40px',
  position: 'relative',
  overflow: 'hidden',
})

const ChooseMe = styled('div')({
  borderRadius: 50,
  width: 16,
  height: 16,
  padding: 1,
  backgroundColor: '#e33250',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 1,
  animation: 'pulse 1s infinite',

  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.3)',
    },
    '100%': {
      transform: 'scale(1)',
    }
  },

  '&.valid': {
    backgroundColor: '#88d232',
    animation: 'none',
  }
});

const ResizedCheckIcon = styled(CheckIcon)({
  color: '#fff',
  fontSize: 14,
});

const ResizedCloseIcon = styled(CloseIcon)({
  color: '#fff',
  fontSize: 14,
})

const NoneIndicator = styled('div')({
  width: '2px',
  height: '100%',
  backgroundColor: '#666',
  transform: 'rotate(-45deg)',
  position: 'absolute',
  top: 0,
  left: '50%',
  marginLeft: '-1px',
})

export class LabOptionValue extends Immutable.Record<{
  name: string,
  slug: string,
  color?: LabOptionColor,
}>({
  name: '',
  slug: '',
  color: undefined,
}) { }

type Props = {
  type: 'option' | 'detail'
  layout: 'color' | 'icon' | 'img-badge'
  name: string,
  option: string,
  listValues: Immutable.OrderedMap<string, LabOptionValue>
}

export default function LabOptionTool(props: Props) {
  const dispatch = useAppDispatch();
  const activeProductId = useAppSelector(state => state.get('UIData').get('designLab').get('activeProductId'));
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(activeProductId)));
  const activeOptions = useAppSelector(state => state.get('UIData').get('designLab').get('activeOptions'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const onIconClick = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (open) {
      setAnchorEl(null)
    } else {
      setAnchorEl(e.currentTarget)
    }
  }, [open]);

  const onMenuClose = useCallback(() => setAnchorEl(null), []);

  const isSelected = useMemo(() => Boolean(activeOptions.get(props.option)), [activeOptions, props.option]);

  let badgeIconSrc = null
  if (props.layout === 'img-badge' && activeOptions.has(props.option)) {
    badgeIconSrc = require('@resources/img/create/design-lab-v4/icons/options/' + props.option + '/' + activeOptions.get(props.option) + '.jpg');
  }

  const iconSrc = useMemo(() => {
    if (!productData) return null

    if (props.layout === 'icon') {
      const iconName = activeOptions.get(props.option) ?? 'none';
      return require('@resources/img/create/design-lab-v4/icons/options/' + productData.get('slug') + '_' + props.option + '/' + iconName + '.svg?url');
    } else {
      const filename = props.type === 'option' ? productData.get('slug') + '_' + props.option : props.option;
      return require('@resources/img/create/design-lab-v4/icons/options/' + filename + '.svg?url')
    }

  }, [productData, props.type, props.layout, activeOptions])


  if (!productData) return null;

  return <>
    <Tooltip title={props.name}>
      <IconButton
        onClick={onIconClick}
        style={{
          padding: '0',
          marginLeft: '15px',
        }}
      >
        {props.type === 'detail' ? <ChooseMe className={isSelected ? 'valid' : ''}>
          {isSelected ? <ResizedCheckIcon /> : <ResizedCloseIcon />}
        </ChooseMe> : null}
        {iconSrc ? <ReactSVG
          src={iconSrc}
          beforeInjection={(svg) => {
            svg.style.display = 'block';
            svg.style.width = '56px';
            svg.style.height = '56px';

            if (props.layout === 'color') {
              const colors = getLabOptionColors(
                props.listValues.get(activeOptions.get(props.option) ?? '')?.get('color')
              );

              if (colors.length === 0) return;

              svg.querySelectorAll('.' + props.option).forEach(elem => {
                elem.setAttribute('fill', colors[0]);
              });

              colors.forEach((color, index) => {
                svg.querySelectorAll(`.${props.option}-${index + 1}`).forEach(elem => {
                  elem.setAttribute('fill', color);
                });
              });
            }
          }}
        /> : null}
        {props.layout === 'color' && activeOptions.get(props.option) === 'none' ? <NoneIndicator /> : null}
        {props.layout === 'img-badge' ? <>
          <Avatar
            src={badgeIconSrc}
            sx={{
              width: 32,
              height: 32,
              position: 'absolute',
              top: '-8px',
              right: '-12px',
              border: '2px solid #fff',
              borderRadius: '50%',
            }}
          />
        </> : null}
      </IconButton>
    </Tooltip>
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onMenuClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          style: {
            borderRadius: props.layout === 'color' ? '50px' : undefined,
            backgroundColor: '#ebebeb',
          }
        }
      }}
    >
      {props.listValues.valueSeq().map(value => {
        const isActive = activeOptions.get(props.option) === value.get('slug');
        
        if (['icon', 'img-badge'].includes(props.layout)) {
          let img
          if (props.layout === 'icon') {
            img = require('@resources/img/create/design-lab-v4/icons/options/' + productData.get('slug') + '_' + props.option + '/' + value.get('slug') + '.svg?url');
          } else {
            img = require('@resources/img/create/design-lab-v4/icons/options/' + props.option + '/' + value.get('slug') + '.jpg');
          }

          return <MenuItem
            key={value.get('slug')}
            sx={{
              backgroundColor: isActive ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
            }}
            onClick={() => {
              dispatch(EditActiveOption(props.option, value.get('slug')));
              setAnchorEl(null);
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={img}
                sx={{
                  width: 48,
                  height: 48,
                }}
              />
              <ListItemText
                primary={value.get('name')}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: isActive ? '600' : 'normal'
                  }
                }}
              />
            </Stack>
          </MenuItem>
        }

        const color = value.get('color')
        const colorBackground = getLabOptionColorBackground(color)

        if (!colorBackground) return null;

        return <Tooltip
          key={value.get('slug')}
          title={value.get('name')}
          placement="left"
        >
          <MenuItem
            sx={{
              backgroundColor: isActive ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
            }}
            onClick={() => {
              dispatch(EditActiveOption(props.option, value.get('slug')));
              setAnchorEl(null);
            }}
          >
            <ColorCircle style={{ background: colorBackground }}>
              {value.get('slug') === 'none' ? <NoneIndicator /> : null}
            </ColorCircle>
          </MenuItem>
        </Tooltip>
      })}
    </Menu>
  </>
}
